/**
 * Recent Documents Component
 * Displays a dropdown of recently opened documents in the status bar
 * (history from the injected DocumentHistory)
 */

import Dropdown from 'bootstrap/js/src/dropdown.js';
import { escapeHtml } from '../core/utilities/dom';
import { formatFileSize } from '../core/utilities/formatting';
import { BaseComponent, type BaseComponentOptions } from './base-component';
import { confirmDialog } from './confirm-dialog';
import type { DocumentEntry } from '../core/document-history';

// DocumentHistory interface
export interface DocumentHistory {
  getHistory(): DocumentEntry[];
  clearHistory(): void;
}

// FileHandleManager interface
export interface FileHandleManager {
  hasFileHandle(docId: string): boolean;
}

// Recent documents options interface
export interface RecentDocumentsOptions extends BaseComponentOptions {
  documentHistory?: DocumentHistory;
  onDocumentSelect?: (doc: DocumentEntry) => void;
  fileHandleManager?: FileHandleManager;
}

class RecentDocuments extends BaseComponent {
  private documentHistory?: DocumentHistory;
  private onDocumentSelect: (doc: DocumentEntry) => void;
  private fileHandleManager?: FileHandleManager;
  private dropdownButton: HTMLButtonElement | null;
  private dropdownMenu: HTMLUListElement | null;

  constructor(options: RecentDocumentsOptions = {}) {
    super(options);
    this.documentHistory = options.documentHistory;
    this.onDocumentSelect = options.onDocumentSelect || (() => {});
    this.fileHandleManager = options.fileHandleManager;
    this.dropdownButton = null;
    this.dropdownMenu = null;
  }

  protected getDefaultCssClass(): string {
    return 'recent-documents';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'recentDocs.button': 'Recent',
      'recentDocs.title': 'Recent Documents',
      'recentDocs.noDocuments': 'No recent documents',
      'recentDocs.clearHistory': 'Clear History',
      'recentDocs.clearConfirm':
        'Are you sure you want to clear document history?',
      'recentDocs.clearTitle': 'Clear document history?',
      'recentDocs.clear': 'Clear',
      'common.cancel': 'Cancel',
    };
  }

  protected override onInitialize(): void {
    this.attachEventListeners();
  }

  /**
   * Render the recent documents dropdown HTML
   */
  protected override render(): void {
    const documents = this.getDocuments();

    if (!this.container) return;

    // Build dropdown HTML
    this.container.innerHTML = `
      <div class="dropup ${this.cssClass}">
        <button class="btn btn-sm btn-light dropdown-toggle ${this.baseCssClass}__button"
                type="button"
                id="recentDocsButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                title="${this.t('recentDocs.title')}">
          <span class="${this.baseCssClass}__icon">📁</span>
          <span class="${this.baseCssClass}__label">${this.t('recentDocs.button')}</span>
        </button>
        <ul class="dropdown-menu ${this.baseCssClass}__menu" aria-labelledby="recentDocsButton">
          ${this.renderDocumentItems(documents)}
        </ul>
      </div>
    `;

    // Store references to key elements
    this.dropdownButton = this.container.querySelector('#recentDocsButton');
    this.dropdownMenu = this.container.querySelector(
      `.${this.baseCssClass}__menu`
    );
  }

  /**
   * Render document items in the dropdown
   * @param documents - Array of document metadata
   * @returns HTML string for dropdown items
   */
  renderDocumentItems(documents: DocumentEntry[]): string {
    if (!documents || documents.length === 0) {
      return `
        <li>
          <span class="dropdown-item-text text-muted ${this.baseCssClass}__empty">
            ${this.t('recentDocs.noDocuments')}
          </span>
        </li>
      `;
    }

    const items = documents.map((doc) => this.renderDocumentItem(doc)).join('');

    const divider = '<li><hr class="dropdown-divider"></li>';

    const clearButton = `
      <li>
        <a class="dropdown-item text-danger ${this.baseCssClass}__clear"
           href="#"
           data-action="clear-history">
          ${this.t('recentDocs.clearHistory')}
        </a>
      </li>
    `;

    return items + divider + clearButton;
  }

  /**
   * Render a single document item
   * @param doc - Document metadata
   * @returns HTML string for document item
   */
  renderDocumentItem(doc: DocumentEntry): string {
    const timeAgo = this.formatTimeAgo(doc.openedAt);
    const fileSize = formatFileSize(doc.size);

    // Check if file can be reopened (has a stored file handle)
    const canReopen =
      this.fileHandleManager && this.fileHandleManager.hasFileHandle(doc.id);
    const reopenIndicator = canReopen
      ? `<span class="${this.baseCssClass}__reopen-indicator" title="Can be reopened directly">↻</span>`
      : '';

    return `
      <li>
        <a class="dropdown-item ${this.baseCssClass}__item"
           href="#"
           data-doc-id="${doc.id}"
           data-action="select-document"
           title="${escapeHtml(doc.name)}">
          <div class="${this.baseCssClass}__doc-name">${reopenIndicator}${escapeHtml(doc.displayName)}</div>
          <div class="${this.baseCssClass}__doc-meta">${fileSize} • ${timeAgo}</div>
        </a>
      </li>
    `;
  }

  /**
   * Format time ago from timestamp
   * @param timestamp - Timestamp in milliseconds
   * @returns Formatted time ago string
   */
  formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      const translated = this.t('recentDocs.openedTime.justNow');
      // Return fallback if translation key returned unchanged
      return translated.includes('recentDocs.') ? 'Just now' : translated;
    } else if (minutes < 60) {
      const translated = this.t('recentDocs.openedTime.minutesAgo', {
        minutes,
      });
      return translated.includes('recentDocs.')
        ? `${minutes} min ago`
        : translated;
    } else if (hours < 24) {
      const translated = this.t('recentDocs.openedTime.hoursAgo', { hours });
      return translated.includes('recentDocs.')
        ? `${hours} hr ago`
        : translated;
    } else {
      const translated = this.t('recentDocs.openedTime.daysAgo', {
        count: days,
      });
      return translated.includes('recentDocs.')
        ? `${days} days ago`
        : translated;
    }
  }

  /**
   * Attach event listeners to dropdown elements
   */
  attachEventListeners(): void {
    if (!this.dropdownMenu) return;

    // Use event delegation for dropdown items
    this.dropdownMenu.addEventListener('click', (e: MouseEvent) => {
      e.preventDefault();

      const target = (e.target as HTMLElement).closest('[data-action]');
      if (!target) return;

      const action = (target as HTMLElement).dataset.action;

      if (action === 'select-document') {
        this.handleDocumentSelect((target as HTMLElement).dataset.docId || '');
      } else if (action === 'clear-history') {
        void this.handleClearHistory();
      }
    });
  }

  /**
   * Handle document selection
   * @param docId - Document ID
   */
  handleDocumentSelect(docId: string): void {
    const documents = this.getDocuments();
    const doc = documents.find((d) => d.id === docId);

    if (doc) {
      // Close the dropdown
      this.closeDropdown();

      // The one load path (#125): a stored analysis (#98 part 4) is reopened
      // from its source, a file from its stored handle or the file picker.
      this.onDocumentSelect(doc);
    }
  }

  /**
   * Handle clear history action
   */
  async handleClearHistory(): Promise<void> {
    // Ask for confirmation
    const confirmed = await confirmDialog({
      title: this.t('recentDocs.clearTitle'),
      message: this.t('recentDocs.clearConfirm'),
      confirmLabel: this.t('recentDocs.clear'),
      cancelLabel: this.t('common.cancel'),
      danger: true,
    });

    if (confirmed) {
      this.clearDocuments();
      this.updateHistory();

      // Close the dropdown
      this.closeDropdown();
    }
  }

  /**
   * Close the dropdown programmatically
   */
  closeDropdown(): void {
    if (this.dropdownButton) {
      Dropdown.getInstance(this.dropdownButton)?.hide();
    }
  }

  /**
   * Update the dropdown with current history
   */
  updateHistory(): void {
    if (!this.dropdownMenu) return;

    const documents = this.getDocuments();

    this.dropdownMenu.innerHTML = this.renderDocumentItems(documents);
  }

  /**
   * Update UI text when language changes
   */
  override updateUILanguage(): void {
    // Update button text
    if (this.dropdownButton) {
      this.dropdownButton.title = this.t('recentDocs.title');

      const label = this.dropdownButton.querySelector(
        `.${this.baseCssClass}__label`
      );
      if (label) {
        label.textContent = this.t('recentDocs.button');
      }
    }

    // Re-render the dropdown menu items with new translations
    this.updateHistory();
  }

  private getDocuments(): DocumentEntry[] {
    return this.documentHistory?.getHistory() ?? [];
  }

  private clearDocuments(): void {
    this.documentHistory?.clearHistory();
  }

  /**
   * Destroy the component and clean up
   */
  override destroy(): void {
    this.dropdownButton = null;
    this.dropdownMenu = null;
    super.destroy();
  }
}

export default RecentDocuments;
